import { useNavigate } from 'react-router-dom';

import {
  ActionStickerCardProps,
  DataLockedAndLoadedCard,
  ProfileChecklist,
} from '@backrs_ui/common-ui';

import { APP_ROUTE } from '@backrs_ui/grow-ui/src/App';
import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { selectContentCompetencies } from '@backrs_ui/grow-ui/src/redux/contentCompetency/contentCompetency';
import { useAppDispatch, useAppSelector } from '@backrs_ui/grow-ui/src/redux/hooks';
import { AppDispatch } from '@backrs_ui/grow-ui/src/redux/store';

import {
  DATA_LOCKED_AND_LOADED_SECTION,
  PROFILE_CHECKLIST_SECTION,
} from './completeYourProfileSection';
import { EXPLORE_OPPORTUNITIES_SECTION } from './exploreOpportunitiesSection';
import { GREAT_COLLEGE_SECTION, SCORE_JOB_INTERNSHIP_SECTION } from './getBackedSection';
import {
  ATTAIN_FINANCIAL_FREEDOM_SECTION,
  BE_WELL_SECTION,
  MASTER_MINDSETS_SECTION,
} from './personalHealthWellnessSection';
import {
  BUILD_SOCIAL_CAPITAL_SECTION,
  BUILD_STRONG_PROFILE_SECTION,
  EXPLORE_CAREER_SECTION,
} from './upYourGameSection';
import { HomePageSectionProps } from '../molecules/HomePageSection';
const HOME_PAGE_STRINGS = appStrings.modules.home.sections;

export type ActionStickerCardSection = Readonly<{
  title: string;
  description: string;
  actionStickerCards: ActionStickerCardProps[];
}>;

export type HomePageSection = Readonly<{
  navigate: ReturnType<typeof useNavigate>;
  getCompetencyUrl: (competencyTitle: string) => string;
  dispatch: AppDispatch;
}>;

export const useGetHomePageSections = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const competencies = useAppSelector(selectContentCompetencies).contentCompetencies;

  const getCompetencyUrl = (competencyTitle: string) => {
    const competencySlug = competencies.find(
      (competency) => competency.title.toLowerCase() === competencyTitle.toLowerCase(),
    )?.slug;

    // if we can't find the competency for some reason we'll just go to prosper
    return competencySlug ? `${APP_ROUTE.COMPETENCY}/${competencySlug}` : APP_ROUTE.PROSPER;
  };

  const sectionParams: HomePageSection = { navigate, getCompetencyUrl, dispatch };

  const getBackedYourWay: HomePageSectionProps = {
    title: HOME_PAGE_STRINGS.getBackedYourWay.title,
    description: HOME_PAGE_STRINGS.getBackedYourWay.description,
    content: {
      type: 'ACTION_STICKER',
      actionStickerSections: [
        GREAT_COLLEGE_SECTION(sectionParams),
        SCORE_JOB_INTERNSHIP_SECTION(sectionParams),
      ],
    },
  };

  const exploreOpportunities: HomePageSectionProps = {
    title: HOME_PAGE_STRINGS.exploreOpportunities.title,
    description: HOME_PAGE_STRINGS.exploreOpportunities.description,
    content: {
      type: 'IMAGE_CARD',
      imageCardSections: EXPLORE_OPPORTUNITIES_SECTION(sectionParams),
    },
  };

  const upYourGame: HomePageSectionProps = {
    title: HOME_PAGE_STRINGS.upYourGame.title,
    description: HOME_PAGE_STRINGS.upYourGame.description,
    content: {
      type: 'ACTION_STICKER',
      actionStickerSections: [
        BUILD_STRONG_PROFILE_SECTION(sectionParams),
        EXPLORE_CAREER_SECTION(sectionParams),
        BUILD_SOCIAL_CAPITAL_SECTION(sectionParams),
      ],
    },
  };

  const completeYourProfile: HomePageSectionProps = {
    title: HOME_PAGE_STRINGS.completeYourProfile.title,
    description: HOME_PAGE_STRINGS.completeYourProfile.description,
    content: {
      type: 'ELEMENT',
      elements: [
        ProfileChecklist({ ...PROFILE_CHECKLIST_SECTION }),
        DataLockedAndLoadedCard({ ...DATA_LOCKED_AND_LOADED_SECTION }),
      ],
    },
  };

  const personalHealthWellness: HomePageSectionProps = {
    title: HOME_PAGE_STRINGS.personalHealthAndWellness.title,
    description: HOME_PAGE_STRINGS.personalHealthAndWellness.description,
    content: {
      type: 'ACTION_STICKER',
      actionStickerSections: [
        ATTAIN_FINANCIAL_FREEDOM_SECTION(sectionParams),
        BE_WELL_SECTION(sectionParams),
        MASTER_MINDSETS_SECTION(sectionParams),
      ],
    },
  };

  return {
    getBackedYourWay,
    exploreOpportunities,
    upYourGame,
    completeYourProfile,
    personalHealthWellness,
  };
};
